import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApiService } from '../../services/adminApi';
import { tokenManager } from '../../services/api';

// Enhanced async thunks with Bearer token security and error handling
export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await adminApiService.events.getAll(params);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch events');
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      return await adminApiService.events.create(eventData);
    } catch (error) {
      console.error('Failed to create event:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to create event');
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, eventData }, { rejectWithValue }) => {
    try {
      return await adminApiService.events.update(id, eventData);
    } catch (error) {
      console.error('Failed to update event:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to update event');
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      await adminApiService.events.delete(eventId);
      return eventId;
    } catch (error) {
      console.error('Failed to delete event:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete event');
    }
  }
);

export const duplicateEvent = createAsyncThunk(
  'events/duplicateEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      return await adminApiService.events.duplicate(eventId);
    } catch (error) {
      console.error('Failed to duplicate event:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to duplicate event');
    }
  }
);

export const fetchEvent = createAsyncThunk(
  'events/fetchEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      return await adminApiService.events.getById(eventId);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch event');
    }
  }
);

const eventSlice = createSlice({
  name: 'events',
  initialState: {
    events: [],
    currentEvent: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setEvents: (state, action) => {
      state.events = action.payload;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch events
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always get an array
        state.events = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Create event
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new event to the list, ensuring events is always an array
        const currentEvents = Array.isArray(state.events) ? state.events : [];
        state.events = [...currentEvents, action.payload];
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update event
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const currentEvents = Array.isArray(state.events) ? state.events : [];
        const index = currentEvents.findIndex(event => event.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete event
      .addCase(deleteEvent.fulfilled, (state, action) => {
        const currentEvents = Array.isArray(state.events) ? state.events : [];
        state.events = currentEvents.filter(event => event.id !== action.payload);
      })
      // Duplicate event
      .addCase(duplicateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(duplicateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const currentEvents = Array.isArray(state.events) ? state.events : [];
        state.events = [...currentEvents, action.payload];
      })
      .addCase(duplicateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch single event
      .addCase(fetchEvent.fulfilled, (state, action) => {
        state.currentEvent = action.payload;
      });
  },
});

export const { clearCurrentEvent, clearError, setEvents } = eventSlice.actions;
export default eventSlice.reducer;
